import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Newspaper } from "lucide-react";
import TimePicker from "@/components/TimePicker";
import RefineChat from "@/components/RefineChat";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsSubscribeForm({ onPreview, previewing }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const signedIn = !!user;
  // For signed-in users we lock the name/email to the account. The
  // form still POSTs the same payload — this just removes the
  // duplicate-entry footgun where a typo would silently create a
  // detached subscription attached to the wrong subscriber row.
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [refinedPrompt, setRefinedPrompt] = useState("");
  const [topicsList, setTopicsList] = useState([]); // [{label, prompt}]
  const [deliveryTime, setDeliveryTime] = useState("07:00");
  const [submitting, setSubmitting] = useState(false);

  // AuthContext resolves `user` asynchronously (post-mount fetch of
  // /api/auth/me), so we can't rely on useState's initializer to
  // capture it. Sync the form's identity fields when the user
  // becomes available.
  useEffect(() => {
    if (user) {
      if (user.email) setEmail(user.email);
      const fn = user.first_name || user.name || "";
      if (fn) setFirstName(fn);
    }
  }, [user]);

  const canSubmit =
    firstName.trim() && /.+@.+\..+/.test(email) && refinedPrompt.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Fill in name, email, and lock in your news interest.");
      return;
    }
    setSubmitting(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      await axios.post(`${API}/news/subscribe`, {
        first_name: firstName.trim(),
        email: email.trim(),
        refined_prompt: refinedPrompt,
        topics: topicsList.map((t) => t.prompt),
        topic_labels: topicsList.map((t) => t.label || ""),
        delivery_time: deliveryTime,
        timezone: tz,
      });
      if (signedIn) {
        // Verified users don't need the check-your-inbox screen —
        // route straight to their dashboard's News tab so they SEE
        // the new subscription land.
        toast.success("News interest added. Loading your feed…");
        navigate("/app?tab=news", { replace: true });
      } else {
        navigate("/check-email", { state: { email: email.trim(), flavor: "news" } });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscribe failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 space-y-6" data-testid="news-subscribe-form">
      {/* Name + Email — hidden for signed-in users (already known
          from the account). Prevents the "typed-a-different-email"
          footgun where a subscription would silently attach to a
          detached subscriber row. */}
      {signedIn ? (
        <div className="border border-black/15 bg-[#FFFBE6] px-4 py-3 text-xs text-neutral-700" data-testid="news-signed-in-banner">
          Adding this feed to <span className="font-semibold text-black">{email}</span>.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="ticker-label block mb-2">First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="e.g. Avery"
              data-testid="news-first-name"
              className="w-full px-4 py-3 border border-black bg-white outline-none focus:bg-[#FFFBE6]"
            />
          </div>
          <div>
            <label className="ticker-label block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com"
              data-testid="news-email"
              className="w-full px-4 py-3 border border-black bg-white outline-none focus:bg-[#FFFBE6]"
            />
          </div>
        </div>
      )}

      {/* News category via chat refinement */}
      <div>
        <label className="ticker-label block mb-2">News category</label>
        <RefineChat
          locked={!!refinedPrompt}
          onLock={(p, topics) => {
            setRefinedPrompt(p);
            setTopicsList(topics || []);
            toast.success("News interest locked in.");
            onPreview?.(p, topics || []);
          }}
        />
        {refinedPrompt && (
          <button
            type="button"
            onClick={() => setRefinedPrompt("")}
            data-testid="news-reopen-refine"
            className="mt-3 text-[11px] uppercase tracking-widest underline text-neutral-600"
          >
            Change my news interest
          </button>
        )}
      </div>

      {/* Delivery time */}
      <div>
        <label className="ticker-label block mb-2">Send me the news at</label>
        <TimePicker value={deliveryTime} onChange={setDeliveryTime} />
        <div className="ticker-label mt-2 !text-[10px]">Your local time</div>
      </div>

      <div className="pt-4 flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          data-testid="news-submit-button"
          className="btn-brutal flex-1 min-w-[220px] py-4 text-sm tracking-[0.14em] uppercase font-semibold inline-flex items-center justify-center gap-3 disabled:opacity-40"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Newspaper className="w-4 h-4" />}
          Start my daily news
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onPreview?.(refinedPrompt, topicsList)}
          disabled={!refinedPrompt || previewing}
          data-testid="news-preview-button"
          className="inline-flex items-center gap-3 px-5 py-4 text-sm tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors disabled:opacity-40"
        >
          {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Preview news
        </button>
      </div>
    </form>
  );
}
